import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../context/AdminContext';
import type { ReferencesMetadata, TagReference } from '@pineseed/spec';

export interface TagMultiSelectorProps {
  value?: ReferencesMetadata;
  onChange: (value: ReferencesMetadata) => void;
  disabled?: boolean;
}

export function TagMultiSelector({ value = [], onChange, disabled = false }: TagMultiSelectorProps) {
  const { adminApp, tagger } = useAdmin();
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(
    new Set(value.map((ref) => ref.tagId))
  );
  const [descriptions, setDescriptions] = useState<Map<string, string>>(
    new Map(value.map((ref) => [ref.tagId, ref.description]))
  );

  // Load tags on mount
  useEffect(() => {
    if (tagger.entities.length === 0 && !tagger.loading) {
      tagger.loadEntities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync with external value changes
  useEffect(() => {
    if (value) {
      setSelectedTagIds(new Set(value.map((ref) => ref.tagId)));
      setDescriptions(new Map(value.map((ref) => [ref.tagId, ref.description])));
    }
  }, [value]);

  const handleToggleTag = useCallback((tagId: string) => {
    setSelectedTagIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(tagId)) {
        updated.delete(tagId);
        setDescriptions((prevDesc) => {
          const updatedDesc = new Map(prevDesc);
          updatedDesc.delete(tagId);
          return updatedDesc;
        });
      } else {
        updated.add(tagId);
      }
      
      // Build references array
      const references: ReferencesMetadata = Array.from(updated).map((id) => ({
        tagId: id,
        description: descriptions.get(id) || '',
      }));
      
      onChange(references);
      return updated;
    });
  }, [descriptions, onChange]);

  const handleDescriptionChange = useCallback((tagId: string, description: string) => {
    setDescriptions((prev) => {
      const updated = new Map(prev);
      updated.set(tagId, description);
      
      // Build references array
      const references: ReferencesMetadata = Array.from(selectedTagIds).map((id) => ({
        tagId: id,
        description: updated.get(id) || '',
      }));
      
      onChange(references);
      return updated;
    });
  }, [selectedTagIds, onChange]);

  if (tagger.loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading tags...</div>;
  }

  if (tagger.error) {
    return <div className="text-sm text-red-500">Error loading tags: {tagger.error}</div>;
  }

  if (tagger.entities.length === 0) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">No tags available</div>;
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        References (Tags)
      </label>
      
      {/* Tag selection */}
      <div className="border border-gray-300 dark:border-gray-600 rounded p-3 bg-white dark:bg-gray-800 max-h-60 overflow-y-auto">
        {tagger.entities.map((tag) => {
          const isSelected = selectedTagIds.has(tag.id);
          return (
            <div key={tag.id} className="mb-3 last:mb-0">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleTag(tag.id)}
                  disabled={disabled}
                  className="mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {tag.name}
                  </div>
                  {tag.description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {tag.description}
                    </div>
                  )}
                  
                  {/* Description input (only shown when selected) */}
                  {isSelected && (
                    <input
                      type="text"
                      value={descriptions.get(tag.id) || ''}
                      onChange={(e) => handleDescriptionChange(tag.id, e.target.value)}
                      disabled={disabled}
                      placeholder="Why is this tag referenced?"
                      className="mt-2 w-full text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  )}
                </div>
              </label>
            </div>
          );
        })}
      </div>
      
      {/* Selected count */}
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {selectedTagIds.size} tag{selectedTagIds.size !== 1 ? 's' : ''} selected
      </div>
    </div>
  );
}

